import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `activity` em português do Brasil. Declarado, nunca inferido, para que uma falta quebre o
 * `tsc`.
 *
 * ── Execução é feminino, e isso decide quase todos os rótulos desta tela ──
 *
 * *run* é **execução** (ADR-0018), e toda a coluna de status é particípio: *concluída*,
 * *cancelada*, *expirada*, *suspensa*. O inglês não marca nada disso — "completed" serve para
 * qualquer coisa — então essa concordância não está no original e não dá para inferi-la de
 * lá. É o caso exemplar de por que o glossário traz a coluna de gênero.
 *
 * Pelo mesmo motivo, *base* é feminino (**nunca citada**), *arquivo* e *documento* são
 * masculinos (**indexados**, **produzidos**), e *busca* é feminino (**vazias**).
 *
 * ── Por que as falhas começam pelo verbo ──
 *
 * `PanelFailure` recebia um sintagma nominal em inglês *com artigo* e o usava como SUJEITO da
 * frase ("Usage and spend could not be loaded"). Em português isso obrigaria o particípio a
 * concordar com seis substantivos diferentes que a frase nunca vê. Todas as seis começam por
 * **"Não foi possível carregar…"**, que não concorda com nada — é a reestruturação que o D-20
 * pede, e não um contorno (PRD-27 F-10, D-21).
 *
 * ── Termos que ficam em inglês ──
 *
 * `p95`, `ctx`, `MTok`, `token`, `embedding` e `Embeddings` permanecem como estão (D-15): é
 * como um operador brasileiro lê esses nomes em qualquer ferramenta que use, e *"vetorização
 * de contexto"* seria menos claro, não mais.
 *
 * ── Os hedges são carga, não enfeite ──
 *
 * A latência do card é a **mediana** e nunca *média* — são perguntas diferentes sobre os
 * mesmos dados. "não diz nada sobre o quanto qualquer coisa está cheia" é o ponto inteiro da
 * linha de falha do armazenamento. E "ainda sem dados suficientes" é uma recusa a informar
 * uma taxa, não uma taxa de zero.
 */

/** O mesmo pacote estrutural do catálogo inglês. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

const plural = (fmt: Counting, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

const files = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} arquivo`, other: `${fmt.formatCount(count)} arquivos` })

const passages = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} trecho`, other: `${fmt.formatCount(count)} trechos` })

const bases = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} base`, other: `${fmt.formatCount(count)} bases` })

const knowledgeBases = (fmt: Counting, count: number): string =>
  plural(fmt, count, {
    one: `${fmt.formatCount(count)} base de conhecimento`,
    other: `${fmt.formatCount(count)} bases de conhecimento`
  })

const runsCount = (fmt: Counting, count: number): string =>
  plural(fmt, count, { one: `${fmt.formatCount(count)} execução`, other: `${fmt.formatCount(count)} execuções` })

/** A mesma cauda nas seis falhas de painel, escrita uma vez. */
const RETRY = 'A plataforma não respondeu — tente de novo, e confira se a API está acessível se continuar falhando.'

export const activity: Messages['activity'] = {
  operatorOnly: {
    title: 'Acesso de operador necessário',
    body: 'Esta tela mostra o uso e o gasto de toda a plataforma.'
  },

  title: 'Atividade',
  lead: 'Uso, latência e gasto em toda a plataforma. As execuções listadas abaixo são as suas.',
  rangeDays: (fmt, days) =>
    plural(fmt, days, { one: `${fmt.formatCount(days)} dia`, other: `${fmt.formatCount(days)} dias` }),

  cards: {
    runs: 'Execuções',
    taskSuccess: 'Sucesso das tarefas',
    latency: 'Latência',
    spend: 'Gasto'
  },

  runsPerDay: (fmt, days) => `Execuções por dia nos últimos ${fmt.formatCount(days)} dias.`,
  spendPerDay: (fmt, days) => `Gasto por dia nos últimos ${fmt.formatCount(days)} dias.`,

  /** Concorda com *execuções*: "concluídas". "com falha" é invariável e serve às duas. */
  completedAndFailed: (fmt, completed, failed) =>
    `${fmt.formatCount(completed)} concluídas · ${fmt.formatCount(failed)} com falha`,
  p95: (duration) => `p95 ${duration}`,
  perSuccessfulRun: (cost) => `${cost} por execução bem-sucedida`,

  steady: 'estável',
  againstPrevious: (fmt, days) => `em relação aos ${fmt.formatCount(days)} dias anteriores`,

  dailySpend: 'Gasto diário',
  peak: (cost) => `pico ${cost}`,
  dayTooltip: (fmt, day, cost, runs) => `${day} — ${cost} em ${runsCount(fmt, runs)}`,

  recentRuns: 'Execuções recentes',
  recentRunsHint: 'abra uma execução na conversa dela',
  noRuns: 'Nenhuma execução ainda. Tudo que você pedir ao assistente aparece aqui, com o custo.',

  runsCount,
  /**
   * "3 model · 1 tools" é telegrafia que só funciona em inglês: *modelo* não é contável aí, é
   * o destino da chamada. Em português a chamada é dita, que é o que a linha mede.
   */
  modelCalls: (fmt, count) =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} chamada ao modelo`,
      other: `${fmt.formatCount(count)} chamadas ao modelo`
    }),
  toolCalls: (fmt, count) =>
    plural(fmt, count, { one: `${fmt.formatCount(count)} ferramenta`, other: `${fmt.formatCount(count)} ferramentas` }),

  /**
   * Tudo aqui concorda com *execução*, que é feminino — e é palavra por palavra o mesmo mapa
   * de `chat.runStatus` e `trace.runStatus`, porque o inglês também é o mesmo nos três.
   */
  runStatus: {
    queued: 'na fila',
    running: 'em andamento',
    waiting_for_approval: 'aguardando aprovação',
    waiting_for_input: 'aguardando entrada',
    waiting_for_external: 'aguardando sistema externo',
    suspended: 'suspensa',
    completed: 'concluída',
    partially_completed: 'parcialmente concluída',
    failed: 'falhou',
    cancelled: 'cancelada',
    expired: 'expirada'
  },
  unknownStatus: 'desconhecida',

  byModel: 'Por modelo',
  noModelCalls: 'Nenhuma chamada de modelo nesta janela.',
  callsCount: (fmt, count) =>
    plural(fmt, count, { one: `${fmt.formatCount(count)} chamada`, other: `${fmt.formatCount(count)} chamadas` }),
  tokensInOut: (input, output) => `${input} entrada / ${output} saída`,

  registeredModels: 'Modelos registrados',
  noModels: 'Nenhum modelo está registrado nesta instalação.',
  modelSpec: (contextK, inputPerMTok, outputPerMTok) =>
    `${contextK}k ctx · $${inputPerMTok}/$${outputPerMTok} por MTok`,

  tools: 'Ferramentas',
  noTools: 'Nenhuma ferramenta está registrada nesta instalação.',
  /** As mesmas palavras de `governance`, `approvals` e `trace`: o inglês é o mesmo mapa. */
  risk: {
    read: 'leitura',
    reversible_write: 'escrita reversível',
    external_communication: 'comunicação externa',
    privileged: 'privilegiada',
    financial: 'financeira',
    destructive: 'destrutiva'
  },
  approvalBadge: 'aprovação',
  dataReaches: (destinations) => `os dados chegam a: ${destinations}`,

  /** Nunca *usuários* onde se fala de gente (ADR-0018). */
  topUsers: 'Quem mais usa',
  noActivity: 'Nenhuma atividade nesta janela.',

  busy: {
    runs: 'Carregando as suas execuções…',
    usage: 'Carregando o uso…',
    models: 'Carregando o registro de modelos…',
    tools: 'Carregando o registro de ferramentas…'
  },

  failure: {
    usage: `Não foi possível carregar o uso e o gasto. ${RETRY}`,
    runs: `Não foi possível carregar o histórico de execuções. ${RETRY}`,
    usageByModel: `Não foi possível carregar o uso por modelo. ${RETRY}`,
    models: `Não foi possível carregar o registro de modelos. ${RETRY}`,
    tools: `Não foi possível carregar o registro de ferramentas. ${RETRY}`,
    usageByPerson: `Não foi possível carregar o uso por pessoa. ${RETRY}`
  },

  storage: {
    title: 'Armazenamento',
    measured: (when) => `medido ${when}`,
    busy: 'Medindo o armazenamento…',
    failed:
      'Não foi possível medir o armazenamento. Isso não diz nada sobre o quanto qualquer coisa está cheia — tente de novo, e confira se a API alcança o banco de dados e o volume de artefatos se continuar falhando.',

    documents: 'Documentos',
    vectorIndex: 'Índice vetorial',
    artifacts: 'Artefatos',
    database: 'Banco de dados',

    files,
    passages,
    bases,
    knowledgeBases,

    /** *indexados* e *ilegíveis* concordam com os documentos, que são masculinos. */
    documentsCensus: (fmt, counts) =>
      `${files(fmt, counts.files)} em ${knowledgeBases(fmt, counts.bases)} · ${plural(fmt, counts.indexed, {
        one: `${fmt.formatCount(counts.indexed)} indexado`,
        other: `${fmt.formatCount(counts.indexed)} indexados`
      })}${
        counts.unreadable > 0
          ? ` · ${plural(fmt, counts.unreadable, {
              one: `${fmt.formatCount(counts.unreadable)} ilegível`,
              other: `${fmt.formatCount(counts.unreadable)} ilegíveis`
            })}`
          : ''
      }`,

    vectorCensus: (fmt, counts) =>
      `${passages(fmt, counts.passages)} · ${plural(fmt, counts.dimensions, {
        one: `${fmt.formatCount(counts.dimensions)} dimensão`,
        other: `${fmt.formatCount(counts.dimensions)} dimensões`
      })}${counts.notEmbedded > 0 ? ` · ${fmt.formatCount(counts.notEmbedded)} sem embedding` : ''}`,

    producedByRuns: (fmt, count) =>
      plural(fmt, count, {
        one: `${files(fmt, count)} produzido por execuções`,
        other: `${files(fmt, count)} produzidos por execuções`
      }),

    fullestBase: 'base mais cheia',
    fullestValue: (share, limit) => `${share} de ${limit}`,
    unnamedBaseOf: (owner) => `uma base de ${owner}`,
    nearItsLimit: 'perto do limite',
    noLimitedBase: 'Nenhuma base de conhecimento tem limite de armazenamento ainda.',

    shareOfDatabase: 'fatia do banco de dados',
    /** Concorda com *fatia*, que é feminino. */
    unknownShare: 'desconhecida',
    vectorBreakdown: (index, payload, total) => `${index} de índice · ${payload} de embeddings em ${total} no total`,

    volume: 'volume',
    volumeUsed: (share) => `${share} em uso`,
    freeOfTotal: (free, total) => `${free} livres de ${total}`,
    volumeUnreadable: 'Não foi possível medir o volume. Isto não é uma leitura de zero.',

    reclaimable: (bytes) => `${bytes} recuperáveis`,
    stillStored: (fmt, count) =>
      plural(fmt, count, {
        one: `${fmt.formatCount(count)} documento removido ainda está guardado para que as citações já entregues continuem funcionando.`,
        other: `${fmt.formatCount(count)} documentos removidos ainda estão guardados para que as citações já entregues continuem funcionando.`
      }),
    nothingReclaimable: 'Nada do que foi removido ainda ocupa espaço.',

    tableTitle: 'O que está ocupando espaço',
    shownOfBases: (fmt, shown, total) => `${fmt.formatCount(shown)} de ${bases(fmt, total)}`,
    embeddingsNote:
      'Embeddings são os vetores derivados dos arquivos de uma base, e costumam ser maiores que os próprios arquivos.',
    nothingIndexed: 'Nada foi indexado nesta instalação ainda.',

    columns: {
      base: 'Base de conhecimento',
      files: 'Arquivos',
      passages: 'Trechos',
      embeddings: 'Embeddings',
      size: 'Tamanho',
      share: 'Fatia'
    },

    baseYouCannotOpen: 'Uma base que você não pode abrir',
    /** As duas formas ratificadas da D-37: "Owned by" é *Pertence a*. */
    ownedBy: 'Pertence a',
    ownedByGroup: 'Pertence ao grupo',
    noOwner: 'sem responsável',
    lastAdded: (when) => `última adição ${when}`,
    ofItsLimit: (share) => `${share} do seu limite`
  },

  agents: {
    title: 'Por agente',
    summary: (fmt, counts) =>
      `${plural(fmt, counts.agents, {
        one: `${fmt.formatCount(counts.agents)} agente`,
        other: `${fmt.formatCount(counts.agents)} agentes`
      })} · ${runsCount(fmt, counts.runs)} · ${counts.cost} em ${fmt.formatCount(counts.days)} dias`,
    previewsExcluded: (fmt, count) =>
      plural(fmt, count, {
        one: `${fmt.formatCount(count)} prévia do construtor fora da contagem`,
        other: `${fmt.formatCount(count)} prévias do construtor fora da contagem`
      }),

    failed: 'Não foi possível carregar o uso por agente.',
    busy: 'Carregando o uso por agente…',
    /** *maker* é criador/criadora; "quem cria" serve aos dois e não obriga a escolher (D-21). */
    empty:
      'Nenhum agente executou nesta janela. As conversas de prévia no construtor ficam de fora de propósito — quem cria testando o próprio rascunho não é carga de produção.',

    ungrounded: (fmt, count) =>
      plural(fmt, count, {
        one: 'Um agente tem conhecimento anexado e não citou nada dele nesta janela — o material anexado está sendo pago e não está respondendo nada.',
        other: `${fmt.formatCount(count)} agentes têm conhecimento anexado e não citaram nada dele nesta janela — o material anexado está sendo pago e não está respondendo nada.`
      }),

    succeeded: (rate) => `${rate} com sucesso`,
    tokens: (count) => `${count} tokens`,
    /** Concorda com *execução*. */
    lastRun: (when) => `última ${when}`,

    cited: (fmt, count) =>
      plural(fmt, count, { one: `${fmt.formatCount(count)} citação`, other: `${fmt.formatCount(count)} citações` }),
    /** Concorda com *buscas*. */
    emptyRate: (rate) => `${rate} vazias`,
    ofSpend: (share) => `${share} do gasto`,

    figures: {
      costPerSuccess: 'custo por execução bem-sucedida',
      tokensInOut: 'tokens entrada / saída',
      searches: 'buscas',
      foundNothing: 'não achou nada',
      failedRuns: 'execuções com falha'
    },
    notEnoughYet: 'ainda sem dados suficientes',

    knowledgeTitle: 'O conhecimento em que se apoia',
    unattachedWithCitations: (fmt, count) =>
      plural(fmt, count, {
        one: `Nada está anexado, então este agente busca no que a pessoa que pergunta já pode ler — ${fmt.formatCount(count)} citação dele veio do catálogo inteiro, e não de um conjunto fixo.`,
        other: `Nada está anexado, então este agente busca no que a pessoa que pergunta já pode ler — as ${fmt.formatCount(count)} citações dele vieram do catálogo inteiro, e não de um conjunto fixo.`
      }),
    unattached:
      'Nada está anexado a este agente e nenhuma resposta citou nada. Ele responde a partir do modelo e do que a pessoa colocou na frente dele.',
    /** Concorda com *base*, que é feminino. */
    neverCited: 'nunca citada',

    unusedBases: (fmt, count, days) =>
      plural(fmt, count, {
        one: `Uma base anexada não respondeu nada nos últimos ${fmt.formatCount(days)} dias. Vale olhar antes de valer apagar — ela pode ter a resposta de uma pergunta que ninguém fez este mês, ou pode ter sido anexada há poucos dias.`,
        other: `${fmt.formatCount(count)} bases anexadas não responderam nada nos últimos ${fmt.formatCount(days)} dias. Vale olhar antes de valer apagar — elas podem ter a resposta de uma pergunta que ninguém fez este mês, ou podem ter sido anexadas há poucos dias.`
      })
  }
}
